import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { DatePicker } from "../../../../components/ui/datepicker";
import { TimePickerSelect } from "../../../../components/ui/timepicker";
import { useKeyHandoverReturnSchedule } from "../context/KeyHandoverReturnScheduleContext";
import moment from "moment-timezone";
import LoaderOverlay from "../../../layout/components/LoaderOverlay";
import { AlertTriangle } from "lucide-react";
import { useKeyHandoverStepCounter } from "../hooks/useKeyHandoverStepCounter";
import type { RootState } from "../../../../redux/store";
import { stationDay, stationDayForPicker } from "../../../../util/stationTime";
import { useSelectedLocationTimeZone } from "../../../../util/locationTimeZone";

// Keep UI identical to handover schedule
const dateTimeWrapperClass = "w-full flex flex-col";
const ZERO = { second: 0, millisecond: 0 } as const;
const DEFAULT_START_HOUR = 12;
const MINUTE_STEP: 5 | 10 | 15 | 20 | 30 | 60 = 30;

/**
 * Maps moment.js weekday numbers (0-6, Sunday=0) to API format (mon, tue, etc.)
 */
const WEEKDAY_MAP: Record<number, string> = {
	0: "sun",
	1: "mon",
	2: "tue",
	3: "wed",
	4: "thu",
	5: "fri",
	6: "sat",
};

/**
 * Get opening hours for a specific date's weekday
 * @param location - Location with opening hours
 * @param date - Date to check (in TZ timezone)
 * @returns Opening hour entry or undefined
 */
function getOpeningHoursForDate(
	location: { openingHours?: Array<{ weekday: string; times?: { fromHour?: number; toHour?: number } }> } | null | undefined,
	date: moment.Moment
) {
	if (!location?.openingHours) return undefined;
	const weekday = WEEKDAY_MAP[date.day()];
	return location.openingHours.find((oh) => oh.weekday === weekday);
}

const KeyHandoverReturnScheduleStep = () => {
  const { t, i18n } = useTranslation();
  const { current, total } = useKeyHandoverStepCounter("returnSchedule");
  const {
    orderData,
    availabilities: groupAvailabilities,
    selectedGroup,
    currency,
    isLoading,
    setReturnFrom,
    setReturnTo,
    setReturnDates,
    setNextEnabled,
  } = useKeyHandoverReturnSchedule();

  // Get selected location for opening hours filtering
  const selectedLocation = useSelector(
    (state: RootState) => state.bookingCreation.selectedLocation
  );
  // The zone used across availability and date math: the selected
  // location's (#3219), as the server reads the window.
  const TZ = useSelectedLocationTimeZone();

  const dateButtonClassName =
    "w-full h-[62px] text-base rounded-[12px] border border-black bg-white px-4 text-black justify-start focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:bg-white";
  const timeSelectClassName =
    "w-full h-[62px] text-base rounded-[12px] border border-black bg-white px-4 text-black focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:bg-white";

  // The station day with the LABEL the customer clicked (see the handover step).
  const availabilityKey = React.useCallback((d: Date) => {
    return stationDay(moment(d), TZ).format("DD.MM.YYYY");
  }, [TZ]);

  const isAvailabilitiesLoaded = Boolean(groupAvailabilities);
  const isEmptyAvailabilities =
    isAvailabilitiesLoaded &&
    Object.keys(groupAvailabilities as Record<string, unknown>).length === 0;

  const withTime = React.useCallback(
    (base: moment.Moment, hour: number, minute: number) => {
      const inTz = base.clone().tz(TZ);
      return moment.tz(
        {
          year: inTz.year(),
          month: inTz.month(),
          date: inTz.date(),
          hour,
          minute,
          second: ZERO.second,
          millisecond: ZERO.millisecond,
        },
        TZ
      );
    },
    [TZ]
  );

  // Sync next button on load/revisit
  React.useEffect(() => {
    const hasBoth = Boolean(
      orderData.keyReturnFromDate && orderData.keyReturnToDate
    );
    setNextEnabled(hasBoth);
  }, [setNextEnabled, orderData.keyReturnFromDate, orderData.keyReturnToDate]);

  const getCurrentDays = React.useCallback(() => {
    if (!orderData.keyReturnFromDate || !orderData.keyReturnToDate) return 1;
    return Math.max(
      orderData.keyReturnToDate.diff(orderData.keyReturnFromDate, "days"),
      1
    );
  }, [orderData.keyReturnFromDate, orderData.keyReturnToDate]);

  const formatCurrency = React.useCallback(
    (amount: number) =>
      new Intl.NumberFormat(i18n.language, {
        style: "currency",
        currency,
      }).format(amount),
    [i18n.language, currency]
  );

  const computeEstimatedTotalPrice = React.useCallback(
    (days: number) => {
      // Base service price from selected group (lowest duration tier, lowest price)
      const priceScale = selectedGroup?.priceCategoryInfo?.priceScale ?? [];
      const base = priceScale.length
        ? (() => {
            // Find the minimum durationInMinutes and the lowest price within that duration
            let minDuration = Number.POSITIVE_INFINITY;
            let pricesForMin: number[] = [];
            for (const { durationInMinutes, price } of priceScale) {
              if (durationInMinutes < minDuration) {
                minDuration = durationInMinutes;
                pricesForMin = [price ?? 0];
              } else if (durationInMinutes === minDuration) {
                pricesForMin.push(price ?? 0);
              }
            }
            const minPrice = pricesForMin.length ? Math.min(...pricesForMin) : 0;
            return Number(minPrice.toFixed(2));
          })()
        : 0;

      // Extra days for this return period (first 24h are free)
      const additionalDaysReturn = Math.max(0, days - 1);

      // Extra days for the original handover period (if available)
      const additionalDaysHandover =
        orderData.checkInDate && orderData.checkOutDate
          ? Math.max(
              0,
              orderData.checkOutDate.diff(orderData.checkInDate, "days") - 1
            )
          : 0;

      const perAdditional = 0.5; // €0.50 per additional day
      const total =
        base + (additionalDaysHandover + additionalDaysReturn) * perAdditional;
      return Number(total.toFixed(2));
    },
    [orderData.checkInDate, orderData.checkOutDate, selectedGroup]
  );

  // Disable dates using availability and ensure dates are not before handover end
  const availableDateSet: Set<string> | undefined = React.useMemo(() => {
    if (!groupAvailabilities) return undefined;
    const keys = Object.keys(groupAvailabilities).filter((dateKey) => {
      const timeSlots = (groupAvailabilities as any)[dateKey];
      return Object.values(timeSlots).some((slot: any) =>
        Object.values(slot as any).some((value: any) => Number(value) > 0)
      );
    });
    return keys.length ? new Set(keys) : undefined;
  }, [groupAvailabilities]);

  const isDateDisabled = React.useCallback(
    (date: Date) => {
      // Disallow selecting a return date strictly before the original handover end date
      if (orderData.checkOutDate) {
        const d = stationDay(moment(date), TZ);
        const end = orderData.checkOutDate.clone().tz(TZ).startOf("day");
        if (d.isBefore(end)) return true;
      }
      if (!availableDateSet) return false;
      const key = availabilityKey(date);
      return !availableDateSet.has(key);
    },
    [availableDateSet, availabilityKey, orderData.checkOutDate, TZ]
  );

  // Minimum start time for selected date considering now, original handover end, and opening hours
  const minStartForDate = React.useCallback(
    (date: moment.Moment | null | undefined): string => {
      if (!date) return "00:00";
      
      const now = moment().tz(TZ);
      const dateInTz = date.clone().tz(TZ);
      const isToday = dateInTz.isSame(now, "day");
      
      // Get opening hours for the selected date's weekday
      let minHourFromOpeningHours: number | null = null;
      const openingHour = getOpeningHoursForDate(selectedLocation, dateInTz);
      
      if (openingHour?.times?.fromHour !== undefined) {
        // Add 1 hour to opening time as per requirement
        minHourFromOpeningHours = openingHour.times.fromHour + 1;
      }
      
      // Determine minimum hour based on current time (if today) and opening hours
      let minHour = 0;
      let minMinute = 0;
      
      if (isToday) {
        // If today, can't pick past times
        const minutes = now.minutes();
        const remainder = minutes % MINUTE_STEP;
        let ceilMinutes = remainder === 0 ? minutes : minutes + (MINUTE_STEP - remainder);
        let hour = now.hour();
        if (ceilMinutes >= 60) {
          hour = (hour + 1) % 24;
          ceilMinutes = 0;
        }
        minHour = hour;
        minMinute = ceilMinutes;
      }
      
      // Apply opening hours constraint (opening + 1 hour)
      if (minHourFromOpeningHours !== null) {
        // If opening hours minimum is later, use that
        if (minHourFromOpeningHours > minHour || 
            (minHourFromOpeningHours === minHour && 0 > minMinute)) {
          minHour = minHourFromOpeningHours;
          minMinute = 0;
        }
      }
      
      // If return date equals the original pickup end day, enforce starting after that time
      const originalEnd = orderData.checkOutDate;
      if (originalEnd && dateInTz.isSame(originalEnd, "day")) {
        const orig = originalEnd.hour() * 60 + originalEnd.minute();
        const next = Math.min(
          (Math.floor(orig / MINUTE_STEP) + 1) * MINUTE_STEP,
          23 * 60 + 30
        );
        const nextHour = Math.floor(next / 60);
        const nextMinute = next % 60;
        
        // Use the later of opening hours minimum or original end time
        if (nextHour > minHour || (nextHour === minHour && nextMinute > minMinute)) {
          minHour = nextHour;
          minMinute = nextMinute;
        }
      }
      
      const hh = String(minHour).padStart(2, "0");
      const mm = String(minMinute).padStart(2, "0");
      return `${hh}:${mm}`;
    },
    [orderData.checkOutDate, selectedLocation, TZ]
  );

  // Compute maximum selectable HH:mm for a given date: respect closing hours
  const maxEndForDate = React.useCallback(
    (date: moment.Moment | null | undefined): string | undefined => {
      if (!date) return undefined;
      
      const dateInTz = date.clone().tz(TZ);
      const openingHour = getOpeningHoursForDate(selectedLocation, dateInTz);
      
      if (openingHour?.times?.toHour !== undefined) {
        // Closing hour is the maximum (no +1 hour buffer for closing time)
        const maxHour = openingHour.times.toHour;
        const hh = String(maxHour).padStart(2, "0");
        return `${hh}:00`;
      }
      
      return undefined;
    },
    [selectedLocation, TZ]
  );

  // Utility: parse HH:mm to minutes since midnight
  const hhmmToMinutes = React.useCallback((hhmm: string) => {
    const [h, m] = hhmm.split(":").map(Number);
    return (isNaN(h) ? 0 : h) * 60 + (isNaN(m) ? 0 : m);
  }, []);

  const handleReturnDateChange = React.useCallback(
    (date: moment.Moment | null) => {
      if (date) {
        // Preserve previously selected start time (if any), otherwise default to 12:00
        let preservedHour = orderData.keyReturnFromDate?.hour() ?? DEFAULT_START_HOUR;
        let preservedMinute = orderData.keyReturnFromDate?.minute() ?? 0;
        
        // Ensure the default time respects minimum constraints (current time + opening hours + original end)
        const tempStart = withTime(stationDay(date, TZ), preservedHour, preservedMinute);
        const minTime = minStartForDate(tempStart);
        const [minHour, minMinute] = minTime.split(':').map(Number);
        
        // If preserved time is before the minimum allowed time, use the minimum instead
        if (preservedHour < minHour || (preservedHour === minHour && preservedMinute < minMinute)) {
          preservedHour = minHour;
          preservedMinute = minMinute;
        }
        
        // Also check maximum time constraint (closing hours)
        const maxTime = maxEndForDate(tempStart);
        if (maxTime) {
          const [maxHour, maxMinute] = maxTime.split(':').map(Number);
          // If preserved time is after the maximum allowed time, use the maximum instead
          if (preservedHour > maxHour || (preservedHour === maxHour && preservedMinute > maxMinute)) {
            preservedHour = maxHour;
            preservedMinute = maxMinute;
          }
        }
        
        // The picked calendar day as the station day with that label
        const base = stationDay(date, TZ);
        const start = withTime(base, preservedHour, preservedMinute);

        const prevDays =
          orderData.keyReturnFromDate && orderData.keyReturnToDate
            ? Math.max(
                orderData.keyReturnToDate.diff(
                  orderData.keyReturnFromDate,
                  "days"
                ),
                1
              )
            : 1;
        const end = withTime(
          start.clone().add(prevDays, "day"),
          start.hour(),
          start.minute()
        );
        setReturnFrom(start);
        setReturnTo(end);
        setNextEnabled(Boolean(start && end));
      } else {
        setReturnDates(null, null);
        setNextEnabled(false);
      }
    },
    [withTime, minStartForDate, maxEndForDate, orderData.keyReturnFromDate, orderData.keyReturnToDate, setReturnFrom, setReturnTo, setReturnDates, setNextEnabled, TZ]
  );

  // Guard: if min/max time constraints change, adjust selected time to stay within bounds
  React.useEffect(() => {
    const from = orderData.keyReturnFromDate;
    if (!from) return;
    
    const minStr = minStartForDate(from);
    const minMins = hhmmToMinutes(minStr);
    const currentMins = from.hour() * 60 + from.minute();
    
    let adjustedMins = currentMins;
    let needsAdjustment = false;
    
    // Check minimum constraint
    if (currentMins < minMins) {
      adjustedMins = minMins;
      needsAdjustment = true;
    }
    
    // Check maximum constraint (closing hours)
    const maxStr = maxEndForDate(from);
    if (maxStr) {
      const maxMins = hhmmToMinutes(maxStr);
      if (adjustedMins > maxMins) {
        adjustedMins = maxMins;
        needsAdjustment = true;
      }
    }
    
    if (needsAdjustment) {
      const h = Math.floor(adjustedMins / 60);
      const m = adjustedMins % 60;
      const nextStart = withTime(from, h, m);
      const days = getCurrentDays();
      const nextEnd = withTime(nextStart.clone().add(days, "day"), h, m);
      setReturnFrom(nextStart);
      setReturnTo(nextEnd);
      setNextEnabled(Boolean(nextStart && nextEnd));
    }
  }, [orderData.keyReturnFromDate, minStartForDate, maxEndForDate, hhmmToMinutes, withTime, getCurrentDays, setReturnFrom, setReturnTo, setNextEnabled]);

  return (
    <div className="mb-6">
      {/* Sub headline: Step indicator under main header */}
      <p className="-mt-5 mb-6 text-xs text-muted-foreground text-center" role="status" aria-live="polite">
        {t("book_location.keys.step_counter", {
          current,
          total,
          defaultValue: "Schritt {{current}} von {{total}}",
        })}
      </p>
      {isAvailabilitiesLoaded &&
        (isEmptyAvailabilities ? (
          <div className="pl-5">
            <p>{t("book_location.order_data.error.no_availabilities_for_group")}</p>
          </div>
        ) : (
          <div className={dateTimeWrapperClass}>
            <p className="text-sm text-muted-foreground mb-5">
              {t("book_location.keys.return_data.hint_text", {
                defaultValue:
                  "Bitte wähle Datum, Startzeit und Tage für die Schlüsselrückgabe.",
              })}
            </p>

            {/* Return date */}
            <label className="mb-1 block text-sm font-medium text-black">
              {t("book_location.keys.return_data.return_date", {
                defaultValue: "Rückgabedatum",
              })}
              <span aria-hidden="true" className="text-appRed ml-0.5">*</span>
            </label>
            <DatePicker
              value={
                orderData.keyReturnFromDate
                  ? stationDayForPicker(orderData.keyReturnFromDate, TZ)
                  : null
              }
              onChange={(d) => handleReturnDateChange(d ? moment(d) : null)}
              buttonClassName={dateButtonClassName}
              isDateDisabled={isDateDisabled}
              showClearInTrigger
            />

            {/* Start time and number of days */}
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div className="flex flex-col">
                <label className="mb-1 block text-xs font-medium text-black">
                  {t("general.start_time", { defaultValue: "Startzeit" })}
                  <span aria-hidden="true" className="text-appRed ml-0.5">*</span>
                </label>
                <TimePickerSelect
                  value={
                    orderData.keyReturnFromDate
                      ? orderData.keyReturnFromDate.format("HH:mm")
                      : null
                  }
                  onChange={(hhmm: string | null) => {
                    if (!hhmm || !orderData.keyReturnFromDate) return;
                    const [h, m] = hhmm.split(":").map(Number);
                    const nextStart = withTime(orderData.keyReturnFromDate, h, m);
                    const days = getCurrentDays();
                    const nextEnd = withTime(
                      nextStart.clone().add(days, "day"),
                      h,
                      m
                    );
                    setReturnTo(nextEnd);
                    setReturnFrom(nextStart);
                    setNextEnabled(Boolean(nextStart && nextEnd));
                  }}
                  minuteStep={MINUTE_STEP}
                  start={minStartForDate(orderData.keyReturnFromDate)}
                  end={maxEndForDate(orderData.keyReturnFromDate)}
                  disabled={!orderData.keyReturnFromDate}
                  className={timeSelectClassName}
                  displayFormat="locale"
                  timeZone={TZ}
                />
              </div>
              <div className="flex flex-col">
                <label className="mb-1 block text-xs font-medium text-black">
                  {t("book_location.reservation_details.number_of_days", {
                    defaultValue: "Anzahl der Tage",
                  })}
                  <span aria-hidden="true" className="text-appRed ml-0.5">*</span>
                </label>
                <div className="flex items-center gap-0">
                  <button
                    type="button"
                    className="h-[62px] w-[62px] rounded-l-[12px] rounded-r-none border border-black bg-background text-black text-2xl leading-none flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 relative focus:z-10 disabled:opacity-50"
                    onClick={() => {
                      if (!orderData.keyReturnFromDate) return;
                      const days = Math.max(1, getCurrentDays() - 1);
                      const h = orderData.keyReturnFromDate.hour();
                      const m = orderData.keyReturnFromDate.minute();
                      const newEnd = withTime(
                        orderData.keyReturnFromDate.clone().add(days, "day"),
                        h,
                        m
                      );
                      setReturnTo(newEnd);
                      setNextEnabled(Boolean(orderData.keyReturnFromDate && newEnd));
                    }}
                    disabled={!orderData.keyReturnFromDate}
                    aria-label={t("general.decrease", { defaultValue: "Verringern" })}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={getCurrentDays()}
                    onChange={(e) => {
                      const raw = parseInt(e.target.value, 10);
                      const days = isNaN(raw) ? 1 : Math.max(raw, 1);
                      if (!orderData.keyReturnFromDate) return;
                      const h = orderData.keyReturnFromDate.hour();
                      const m = orderData.keyReturnFromDate.minute();
                      const newEnd = withTime(
                        orderData.keyReturnFromDate.clone().add(days, "day"),
                        h,
                        m
                      );
                      setReturnTo(newEnd);
                      setNextEnabled(Boolean(orderData.keyReturnFromDate && newEnd));
                    }}
                    className={`${timeSelectClassName} text-center rounded-none border-l-0 border-r-0 relative focus:z-10`}
                    disabled={!orderData.keyReturnFromDate}
                    aria-label={t("book_location.reservation_details.number_of_days", {
                      defaultValue: "Anzahl der Tage",
                    })}
                  />
                  <button
                    type="button"
                    className="h-[62px] w-[62px] rounded-r-[12px] rounded-l-none border border-black bg-background text-black text-2xl leading-none flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 relative focus:z-10 disabled:opacity-50"
                    onClick={() => {
                      if (!orderData.keyReturnFromDate) return;
                      const days = getCurrentDays() + 1;
                      const h = orderData.keyReturnFromDate.hour();
                      const m = orderData.keyReturnFromDate.minute();
                      const newEnd = withTime(
                        orderData.keyReturnFromDate.clone().add(days, "day"),
                        h,
                        m
                      );
                      setReturnTo(newEnd);
                      setNextEnabled(Boolean(orderData.keyReturnFromDate && newEnd));
                    }}
                    disabled={!orderData.keyReturnFromDate}
                    aria-label={t("general.increase", { defaultValue: "Erhöhen" })}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            {/* Price info when more than 1 day */}
            {(() => {
              const days = getCurrentDays();
              const additionalDays = Math.max(0, days - 1);
              if (
                !orderData.keyReturnFromDate ||
                !orderData.keyReturnToDate ||
                additionalDays <= 0
              )
                return null;
              const totalPrice = computeEstimatedTotalPrice(days);
              return (
                <p className="mt-3 text-sm text-muted-foreground">
                  <Trans
                    i18nKey="book_location.keys.return_data.extension_price_info"
                    values={{
                      count: additionalDays,
                      additionalDays,
                      totalPrice: formatCurrency(totalPrice),
                    }}
                    components={{ bold: <strong className="font-semibold" /> }}
                    defaults={
                      "Für <bold>{{additionalDays}}</bold> zusätzliche Tag(e) fällt ein Gesamtpreis von <bold>{{totalPrice}}</bold> an."
                    }
                  />
                </p>
              );
            })()}

            {/* Deadline/notice box for return end */}
            {orderData.keyReturnFromDate &&
              orderData.keyReturnToDate &&
              (() => {
                const deadlineMoment = orderData.keyReturnToDate
                  .clone()
                  .tz(TZ)
                  .locale(i18n.language);
                const dateStr = deadlineMoment.format("L");
                const timeStr = deadlineMoment.format("LT");
                const deadlineDisplay = i18n.language.startsWith("de")
                  ? `${dateStr} ${timeStr} Uhr`
                  : `${dateStr} ${timeStr}`;
                return (
                  <div className="mt-3 rounded-[12px] border border-black bg-gray-50 p-3 text-sm text-foreground flex items-start gap-3">
                    <AlertTriangle
                      className="h-5 w-5 text-amber-600 mt-0.5 shrink-0"
                      aria-hidden="true"
                    />
                    <div className="flex-1">
                      <Trans
                        i18nKey="book_location.keys.order_data.deadline_notice"
                        values={{ deadline: deadlineDisplay, fee: formatCurrency(20) }}
                        components={{ bold: <strong className="font-semibold" />, br: <br /> }}
                      />
                    </div>
                  </div>
                );
              })()}
          </div>
        ))}
      <LoaderOverlay open={isLoading} />
    </div>
  );
};

export default KeyHandoverReturnScheduleStep;
